import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'modules/auth/hooks';
import { IEntity } from 'modules/application/types';
import { Mutations } from 'modules/refund';

import { Button, Modal, Status } from 'components';
import * as Details from 'components/Details';
import * as Icon from 'components/Icon';

import { Refund } from './';
import { useRefundJournal } from 'modules/application/hooks';
import { useRefundDelete } from 'modules/application/mutations';
import classes from '../ApplicationView.module.scss';
import * as Table from 'components/Table';
import toast from 'react-hot-toast';
import config from 'config';

interface Props {
  id: number;
  item: IEntity.ApplicationSingle;
}

const RefundButton = ({ id, item }: Props) => {
  const { t, i18n } = useTranslation();
  const [isRefund, setRefund] = useState({
    id: 0,
    level: 0
  });
  const [isJournal, setJournal] = useState(false);
  const [column, setColumn] = useState<any>();
  const { profile } = useAuth();
  const refundMutation = Mutations.useCreate();
  const {
    data: { items },
    refetch
  } = useRefundJournal({ id });
  const refundDelete = useRefundDelete();

  const visibleJournal = items?.some(i =>
    ['IN_PROCESS', 'REVIEW', 'REVIEW_DIRECTOR', 'REVIEW_ACCOUNTANT', 'COMPLETED', 'INITIAL'].includes(i.refund_status)
  );

  const refund = () => {
    refundMutation.mutate(
      { id: item.id },
      {
        onSuccess: ({ id, level }) => {
          if (!!id)
            setRefund({
              id,
              level
            });
        }
      }
    );
  };

  const closeRefund = () => {
    setRefund({
      id: 0,
      level: 0
    });
    refetch();
  };

  const visibleIcon = ({ status, refundId }: { status: string; refundId: number }) => {
    const item = items.find(i => i.id === refundId);

    if (status === 'INITIAL') {
      return (
        <div style={{ width: '24px', cursor: 'pointer' }} onClick={() => setRefund({ id: item.id, level: item.level })}>
          <Icon.System.Edit />
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '24px', cursor: 'pointer' }} onClick={() => setColumn(item)}>
          <Icon.System.Show />
        </div>
        {['IN_PROCESS', 'REVIEW', 'REVIEW_DIRECTOR'].includes(status) && (
          <div
            style={{ width: '24px', cursor: 'pointer' }}
            onClick={() => {
              refundDelete.mutate(
                { id: item.id },
                {
                  onSuccess: () => {
                    refetch();
                    toast.success(t('message_refund_delete_success').toString());
                  },
                  onError: () => {
                    toast.error(t('message_refund_delete_error').toString());
                  }
                }
              );
            }}
          >
            <Icon.System.Delete />
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!items.length) {
      setJournal(false);
    }
  }, [items]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {!visibleJournal && (
          <Button onClick={refund} isLoading={refundMutation.isLoading}>
            {t('Refund_invoice')}
          </Button>
        )}
        {!!items.length && (
          <Button variant="blue" onClick={() => setJournal(true)}>
            {t('refund_journal_list')}
          </Button>
        )}
      </div>
      <Modal isOpen={!!isRefund.id} close={closeRefund} width={900} variant="white">
        <Refund
          id={isRefund.id}
          applicationId={item.id}
          onCancel={closeRefund}
          level={isRefund.level}
          type={profile.type}
        />
      </Modal>
      <Modal isOpen={isJournal} close={() => setJournal(false)} width={900}>
        <div className={classes.tableWrapper}>
          <Table.Table className={classes.table}>
            <Table.Header className={classes.tableHeader}>
              <Table.HeaderCell align={'center'}>{t('id')}</Table.HeaderCell>
              <Table.HeaderCell align={'center'}>{t('refund_sent_date')}</Table.HeaderCell>
              <Table.HeaderCell align={'center'}>{t('refund_completed_date')}</Table.HeaderCell>
              <Table.HeaderCell align={'center'}>{t('refund_status')}</Table.HeaderCell>
              <Table.HeaderCell align={'center'}>{t('refund_show')}</Table.HeaderCell>
            </Table.Header>
            <Table.Body>
              {items.map(item => (
                <Table.Row key={item.id}>
                  <Table.Cell>{item.id}</Table.Cell>
                  <Table.Cell>{item.sent_date}</Table.Cell>
                  <Table.Cell>{item.completed_date}</Table.Cell>
                  <Table.Cell>
                    <Status
                      variant={
                        item.refund_status === 'COMPLETED'
                          ? 'success'
                          : item.refund_status === 'DENIED'
                          ? 'danger'
                          : 'warning'
                      }
                    >
                      {t(item.refund_status)}
                    </Status>
                  </Table.Cell>
                  <Table.Cell>{visibleIcon({ refundId: item.id, status: item.refund_status })}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Table>
        </div>
      </Modal>
      <Modal isOpen={!!column} close={() => setColumn(null)} width={600}>
        <div className={classes.wrapper}>
          <Details.List>
            <Details.Item title={t('service_name')}>{column?.service_name}</Details.Item>
            <Details.Item title={t('application_id')}>{column?.application_id}</Details.Item>
            <Details.Item title={t('refund_sent_date')}>{column?.sent_date}</Details.Item>
            <Details.Item title={t('refund_completed_date')}>{column?.completed_date}</Details.Item>
            <Details.Item title={t('name')}>{column?.name}</Details.Item>
            {!!column?.pin && <Details.Item title={t('pin')}>{column?.pin}</Details.Item>}
            {!!column?.tin && <Details.Item title={t('tin')}>{column?.tin}</Details.Item>}
            <Details.Item title={t('refund_system')}>{column?.refund_system}</Details.Item>
            <Details.Item title={t('refund_status')}>{t(column?.refund_status)}</Details.Item>
            {column?.refund_status === 'COMPLETED' && (
              <Details.Item title={t('refund_organization')}>{t(column?.refund_organization)}</Details.Item>
            )}
            <Details.Item title={t('refund_document')}>
              {column?.uuid && (
                <Button
                  variant="stroke"
                  prefixIcon={<Icon.System.Print />}
                  container={
                    <a
                      href={`${config.document.base_url_v2}/refund/${column?.uuid}/pdf?language=${i18n.language}&download`}
                      target="_blank"
                      rel="noreferrer"
                    />
                  }
                >
                  {t('Print')}
                </Button>
              )}
            </Details.Item>
          </Details.List>
        </div>
      </Modal>
    </div>
  );
};

export default RefundButton;
